import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { getWelcomeIdentity, postTeacherApi } from "./teacherPostsApi";
import { updatePosterLikeReport } from "../../functions/posterReport";
import "./welcome.css";

import homeDesk from "./img/home.png";
import homeMob from "./img/home-mob.png";
import bgDesk from "./img/background-web2.jpg";
import bgMob from "./img/background-mob2.jpg";
import mag1 from "./img/mag/1.jpg";
import mag2 from "./img/mag/2.jpg";
import mag3 from "./img/mag/3.jpg";
import mag4 from "./img/mag/4.jpg";
import mag5 from "./img/mag/5.jpg";
import mag6 from "./img/mag/6.jpg";
import likeOnImg from "./img/like-on.png";
import likeOffImg from "./img/like-off.png";
import removeImg from "./img/remove.png";
import popupLikeImg from "./img/popup-like.png";
import ohnoImg from "./img/ohno.png";

const MAGS = [
  { file: "1.jpg", src: mag1 },
  { file: "2.jpg", src: mag2 },
  { file: "3.jpg", src: mag3 },
  { file: "4.jpg", src: mag4 },
  { file: "5.jpg", src: mag5 },
  { file: "6.jpg", src: mag6 },
];

const QUOTE_LIMIT = 500;

function readStoredUser(user) {
  try {
    return user || JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return user || {};
  }
}

function isMobileDevice() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function showPosterPopup(imageSrc, imageWidth, title = "") {
  return Swal.fire({
    imageUrl: imageSrc,
    imageWidth,
    imageAlt: "",
    title,
    confirmButtonText: "OK",
    buttonsStyling: false,
    customClass: {
      popup: "welcome-popup",
      title: "welcome-popup-title",
      confirmButton: "welcome-popup-ok",
    },
  });
}

export default function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, status } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => readStoredUser(user), [user]);
  const identity = useMemo(() => getWelcomeIdentity(storedUser), [storedUser]);
  const userId = storedUser?.userId || storedUser?.userid || storedUser?.id || "";
  const sessionId = storedUser?.sessionId;
  const organizationId = storedUser?.organizationId;

  const [quote, setQuote] = useState("");
  const [background, setBackground] = useState("1.jpg");
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [likingId, setLikingId] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState("");
  const [showRotate, setShowRotate] = useState(false);

  const backendReady = Boolean(userId && sessionId && organizationId && adminToken);
  const quoteLen = quote.length;
  const canSubmit =
    backendReady && !submitting && quote.trim().length > 0 && quoteLen <= QUOTE_LIMIT;

  useEffect(() => {
    if (storedUser?.backButtonRedirect != null) {
      dispatch(setBackButtonUrl(storedUser.backButtonRedirect));
    }
  }, [storedUser, dispatch]);

  useEffect(() => {
    if (status === "failed" || (status === "succeeded" && !userId)) {
      navigate("/", { replace: true });
    }
  }, [status, userId, navigate]);

  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
    };
    const applyBg = () => {
      const mobile = window.matchMedia("(max-width: 700px)").matches;
      body.classList.remove("common-bg");
      body.style.backgroundImage = `url("${mobile ? bgMob : bgDesk}")`;
      body.style.backgroundRepeat = "no-repeat";
      body.style.backgroundSize = "100% 100%";
      body.style.backgroundPosition = "center center";
      body.style.backgroundAttachment = "fixed";
    };
    applyBg();
    window.addEventListener("resize", applyBg);
    return () => {
      window.removeEventListener("resize", applyBg);
      body.style.backgroundImage = prev.backgroundImage;
      body.style.backgroundSize = prev.backgroundSize;
      body.style.backgroundRepeat = prev.backgroundRepeat;
      body.style.backgroundPosition = prev.backgroundPosition;
      body.style.backgroundAttachment = prev.backgroundAttachment;
    };
  }, []);

  useEffect(() => {
    const checkRotate = () => {
      if (!isMobileDevice()) {
        setShowRotate(false);
        return;
      }
      setShowRotate(window.innerHeight < window.innerWidth);
    };
    checkRotate();
    window.addEventListener("resize", checkRotate);
    window.addEventListener("orientationchange", checkRotate);
    return () => {
      window.removeEventListener("resize", checkRotate);
      window.removeEventListener("orientationchange", checkRotate);
    };
  }, []);

  const loadPosts = useCallback(
    async (nextPage = 1) => {
      if (!backendReady) return;
      setLoadingPosts(true);
      try {
        const { ok, data } = await postTeacherApi("getTeacherPosts", adminToken, {
          userId,
          sessionId,
          organizationId,
          page: nextPage,
        });
        if (!ok || !data.success) return;
        setPosts(Array.isArray(data.posts) ? data.posts : []);
        setPage(Number(data.page) || 1);
        setPageCount(Number(data.pageCount) || 0);
      } catch (err) {
        console.error("loadPosts:", err);
      } finally {
        setLoadingPosts(false);
      }
    },
    [backendReady, adminToken, userId, sessionId, organizationId]
  );

  useEffect(() => {
    loadPosts(1);
  }, [loadPosts]);

  const handleQuoteChange = (e) => {
    const next = String(e.target.value || "").replace(/^\s+/, "");
    setQuote(next.slice(0, QUOTE_LIMIT));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setProgress(12);
    const tick = window.setInterval(() => {
      setProgress((p) => (p >= 90 ? 90 : p + 8));
    }, 180);
    try {
      const { ok, data } = await postTeacherApi("createTeacherPost", adminToken, {
        userId,
        sessionId,
        organizationId,
        name: identity.fullName,
        userType: storedUser?.role || "",
        quote: quote.trim(),
        background,
      });
      window.clearInterval(tick);
      setProgress(100);
      if (!ok || !data.success) {
        await Swal.fire("Error", data.message || "Could not save your quote.", "error");
        return;
      }
      if (identity.isDemo) {
        await Swal.fire("Subscribe to any PLAN to play with your peers.", "", "success");
        const plans = `${String(process.env.REACT_APP_BASE_URL || "").replace(/\/+$/, "")}/plans`;
        window.location.href = plans;
        return;
      }
      navigate("/edit", {
        replace: true,
        state: {
          postId: data.id,
          quote: data.quote || quote.trim(),
          background: data.background || background,
          name: data.name || identity.fullName,
        },
      });
    } catch (err) {
      window.clearInterval(tick);
      await Swal.fire("Error", err.message || "Could not save your quote.", "error");
    } finally {
      window.clearInterval(tick);
      setSubmitting(false);
      setProgress(0);
    }
  };

  const handleLike = async (post) => {
    if (!post || likingId != null) return;
    if (identity.isDemo) {
      await Swal.fire("", "Subscribe to any plan to LIKE posts.", "error");
      return;
    }
    if (post.liked) {
      await showPosterPopup(ohnoImg, 220);
      return;
    }
    setLikingId(post.id);
    try {
      const { ok, data } = await postTeacherApi("likeTeacherPost", adminToken, {
        userId,
        sessionId,
        organizationId,
        postId: post.id,
      });
      if (data.alreadyLiked) {
        await showPosterPopup(ohnoImg, 220);
        await loadPosts(page);
        return;
      }
      if (!ok || !data.success) {
        await Swal.fire("Error", data.message || "Could not like this post.", "error");
        return;
      }
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, liked: true, likes: Number(data.likes) || p.likes + 1 }
            : p
        )
      );
      try {
        await updatePosterLikeReport({
          storedUser,
          reportId: data.reportId,
          points: Number(data.likes) || 0,
        });
      } catch (reportErr) {
        console.error("updatePosterLikeReport:", reportErr);
      }
      await showPosterPopup(popupLikeImg, 220, "Thank you for liking!");
    } catch (err) {
      console.error("handleLike:", err);
    } finally {
      setLikingId(null);
    }
  };

  const handleDelete = async (post) => {
    if (!post?.isOwn) return;
    const confirm = await Swal.fire({
      title: "Delete this post?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Delete",
    });
    if (!confirm.isConfirmed) return;
    const { ok, data } = await postTeacherApi("deleteTeacherPost", adminToken, {
      userId,
      sessionId,
      organizationId,
      postId: post.id,
    });
    if (!ok || !data.success) {
      await Swal.fire("Error", data.message || "Could not delete this post.", "error");
      return;
    }
    await loadPosts(page);
  };

  return (
    <>
      <div className={`welcome-rotate${showRotate ? " is-on" : ""}`}>
        Please rotate your device for the best experience
      </div>

      <div className="welcome-page">
        <div className="welcome-inner">
          <img src={homeDesk} alt="Teacher Says" className="welcome-banner welcome-banner--desk" />
          <img src={homeMob} alt="Teacher Says" className="welcome-banner welcome-banner--mob" />

          <form className="welcome-form" onSubmit={handleSubmit}>
            <div className="welcome-field">
              <label className="welcome-label" htmlFor="welcome-name">
                {identity.nameLabel}
              </label>
              <input
                id="welcome-name"
                className="welcome-input"
                type="text"
                value={identity.fullName}
                readOnly
                required
              />
            </div>

            <div className="welcome-field">
              <span className="welcome-label">Select A Background</span>
              <div className="welcome-mags">
                {MAGS.map((mag) => (
                  <label className="welcome-mag" key={mag.file}>
                    <input
                      type="radio"
                      name="welcome-background"
                      value={mag.file}
                      checked={background === mag.file}
                      onChange={() => setBackground(mag.file)}
                    />
                    <img src={mag.src} alt="" />
                  </label>
                ))}
              </div>
            </div>

            <div className="welcome-field">
              <label className="welcome-label" htmlFor="welcome-quote">
                Type your favourite teacher quote
              </label>
              <div>
                <textarea
                  id="welcome-quote"
                  className="welcome-textarea"
                  rows={4}
                  maxLength={QUOTE_LIMIT}
                  value={quote}
                  onChange={handleQuoteChange}
                  required
                />
                <span className="welcome-counter">
                  {quoteLen}/{QUOTE_LIMIT} Characters
                </span>
              </div>
            </div>

            <div className="welcome-submit-wrap">
              <button type="submit" className="welcome-submit" disabled={!canSubmit}>
                {submitting ? "Saving" : "Submit"}
              </button>
            </div>
            <div className={`welcome-progress${submitting ? " is-on" : ""}`}>
              <div className="welcome-progress-bar" style={{ width: `${progress}%` }}>
                {submitting ? `${progress}% please wait..` : ""}
              </div>
            </div>
          </form>
        </div>

        <div className="welcome-gallery">
          {loadingPosts && posts.length === 0 ? (
            <div className="welcome-empty">Loading posts…</div>
          ) : posts.length === 0 ? (
            <div className="welcome-empty">No Post. Be the first one.</div>
          ) : (
            posts.map((post) => (
              <div className="welcome-card" key={post.id}>
                <img
                  src={post.imageUrl}
                  alt=""
                  className="welcome-card-image"
                  onClick={() => setLightboxUrl(post.imageUrl)}
                />
                <div className="welcome-card-bar">
                  <button
                    type="button"
                    className="welcome-like"
                    disabled={likingId != null}
                    onClick={() => handleLike(post)}
                  >
                    <img src={post.liked ? likeOnImg : likeOffImg} alt="" />
                    <span>{post.likes}</span>
                  </button>
                  <span className="welcome-card-name">{post.name}</span>
                </div>
                {post.isOwn ? (
                  <button
                    type="button"
                    className="welcome-delete"
                    onClick={() => handleDelete(post)}
                    aria-label="Delete post"
                  >
                    <img src={removeImg} alt="" />
                  </button>
                ) : null}
              </div>
            ))
          )}
        </div>

        {pageCount > 1 ? (
          <div className="welcome-pager">
            <button type="button" disabled={page <= 1 || loadingPosts} onClick={() => loadPosts(page - 1)}>
              Previous
            </button>
            {Array.from({ length: pageCount }, (_, i) => (
              <button
                type="button"
                key={i + 1}
                className={page === i + 1 ? "is-active" : undefined}
                disabled={loadingPosts}
                onClick={() => loadPosts(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              type="button"
              disabled={page >= pageCount || loadingPosts}
              onClick={() => loadPosts(page + 1)}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      {lightboxUrl ? (
        <div className="welcome-lightbox" onClick={() => setLightboxUrl("")} role="presentation">
          <button type="button" className="welcome-lightbox-close" aria-label="Close">
            ×
          </button>
          <img src={lightboxUrl} alt="" onClick={(ev) => ev.stopPropagation()} />
        </div>
      ) : null}
    </>
  );
}
